/*
* Mobile-only sticky add-to-cart bar (snippets/sticky-atc-bar.liquid)
*
* - Shows/hides via IntersectionObserver on the main add-to-cart button.
* - Adds to cart by clicking the main form's button, so quantity, selling
*   plans, preorder properties and the theme's cart events all run through
*   the existing ProductAddButton flow in theme.js.
* - Mirrors the main button's disabled/loading/success state and label with
*   MutationObservers instead of duplicating variant logic.
*/

(function () {
  'use strict';

  var VISIBLE_CLASS = 'is-visible';
  var BODY_CLASS = 'sticky-atc-open';

  function motionReduced() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function initBar(bar) {
    if (bar.stickyAtcInit) return;

    var section = bar.closest('[data-section-type="product"]');
    if (!section) return;

    var mainButton = section.querySelector('[data-buttons-wrapper] [data-add-to-cart]');
    if (!mainButton || !('IntersectionObserver' in window)) return;

    bar.stickyAtcInit = true;

    var mainButtonText = mainButton.querySelector('[data-add-to-cart-text]');
    var barButton = bar.querySelector('[data-sticky-atc-button]');
    var barButtonText = bar.querySelector('[data-sticky-atc-button-text]');
    var variantLabel = bar.querySelector('[data-sticky-atc-variant]');
    var thumb = bar.querySelector('[data-sticky-atc-thumb]');
    var liveRegion = bar.querySelector('[data-sticky-atc-live]');
    var productTitle = bar.getAttribute('data-product-title') || '';
    var liveRegionTimer = null;

    /* --- Visibility ---------------------------------------------------- */

    /* Show only once the button has been scrolled past (sits fully above the
       viewport) — not while it is still below, e.g. at the top of the page.
       The root is extended far downward so "at or below the viewport top" is
       a single intersecting state; the flip to non-intersecting (= scrolled
       past) always fires, even when a fast scroll skips the in-view frames. */
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          toggleBar(!entry.isIntersecting && entry.boundingClientRect.top < 0);
        });
      },
      {threshold: 0, rootMargin: '0px 0px 10000px 0px'}
    );
    observer.observe(mainButton);

    function toggleBar(show) {
      bar.classList.toggle(VISIBLE_CLASS, show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      document.body.classList.toggle(BODY_CLASS, show);
    }

    /* --- Variant sync ---------------------------------------------------
       theme.js updates the bar's [data-price-wrapper] prices itself; here we
       only track the variant label, the thumbnail and the button's accessible
       name.

       The thumbnail's starting value is Liquid's job, not ours: theme.js fires
       theme:variant:change once at init, before any section has bound a
       listener, so that first event is dropped and never reaches us. The
       snippet renders the selected variant's shot server-side; this only has
       to keep up with changes after that. */

    section.addEventListener('theme:variant:change', function (event) {
      var variant = event.detail ? event.detail.variant : null;
      if (variantLabel && variant) {
        variantLabel.textContent = variant.title;
      }
      updateVariantThumb(variant);
      updateAriaLabel();
    });

    /* Sized off the variant's own image URL rather than a second lookup: the
       src is protocol-relative and already carries ?v=, so the CDN's sizing
       parameters just append. Sizes match the snippet's 44px box at 1x/2x. */
    function updateVariantThumb(variant) {
      if (!thumb || !variant || !variant.featured_image) return;

      var src = variant.featured_image.src;
      if (!src) return;

      thumb.src = sizedImageUrl(src, 88);
      thumb.srcset = sizedImageUrl(src, 88) + ' 88w, ' + sizedImageUrl(src, 132) + ' 132w';
    }

    function sizedImageUrl(src, size) {
      return src + (src.indexOf('?') === -1 ? '?' : '&') +
        'width=' + size + '&height=' + size + '&crop=center';
    }

    function updateAriaLabel() {
      var label = barButtonText.textContent.trim() + ' - ' + productTitle;
      if (variantLabel && variantLabel.textContent.trim()) {
        label += ' - ' + variantLabel.textContent.trim();
      }
      barButton.setAttribute('aria-label', label);
    }

    /* --- Mirror the main button's state ---------------------------------
       theme.js toggles disabled ("Sold out"/"Unavailable"), .loading and
       .has-success on the main button; copy them so the two can't desync. */

    var stateObserver = new MutationObserver(syncButtonState);
    stateObserver.observe(mainButton, {attributes: true, attributeFilter: ['class', 'disabled']});

    if (mainButtonText) {
      var textObserver = new MutationObserver(syncButtonText);
      textObserver.observe(mainButtonText, {childList: true, subtree: true, characterData: true});
    }

    function syncButtonState() {
      barButton.disabled = mainButton.disabled;
      barButton.classList.toggle('loading', mainButton.classList.contains('loading'));

      var success = mainButton.classList.contains('has-success');
      if (success && !barButton.classList.contains('has-success')) {
        announceSuccess();
      }
      barButton.classList.toggle('has-success', success);
    }

    function syncButtonText() {
      barButtonText.textContent = mainButtonText.textContent.trim();
      updateAriaLabel();
    }

    function announceSuccess() {
      if (!liveRegion) return;
      var completeState = barButton.querySelector('.btn-state-complete');
      liveRegion.textContent = completeState ? completeState.textContent.trim() : barButtonText.textContent.trim();
      clearTimeout(liveRegionTimer);
      liveRegionTimer = setTimeout(function () {
        liveRegion.textContent = '';
      }, 4000);
    }

    /* --- Add to cart ----------------------------------------------------- */

    barButton.addEventListener('click', function () {
      if (barButton.disabled || mainButton.classList.contains('loading')) return;
      mainButton.click();
    });

    /* When the selection is sold out/unavailable the button is disabled;
       tapping anywhere else on the bar scrolls back to the variant options. */
    bar.addEventListener('click', function (event) {
      if (!barButton.disabled || barButton.contains(event.target)) return;

      var target = section.querySelector('.form__selectors') || mainButton;
      target.scrollIntoView({
        behavior: motionReduced() ? 'auto' : 'smooth',
        block: 'center'
      });

      var firstInput = target.querySelector('input:not([type="hidden"]):checked, input:not([type="hidden"]), button, select');
      if (firstInput) {
        firstInput.focus({preventScroll: true});
      }
    });
  }

  function initAll() {
    document.querySelectorAll('[data-sticky-atc]').forEach(initBar);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /* Theme editor: re-init when the product section is re-rendered */
  document.addEventListener('shopify:section:load', initAll);
})();
